import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HiDocumentText,
  HiPhotograph,
  HiUserCircle,
  HiClock,
  HiCheckCircle,
  HiDownload
} from 'react-icons/hi';

import zapblastHero from '../assets/zapblast/zapblast-hero.png';
import screenshot1 from '../assets/zapblast/screenshot_1.png';
import screenshot2 from '../assets/zapblast/screenshot_2.png';

const features = [
  {
    icon: <HiDocumentText className="text-3xl text-blue-600" />,
    title: 'Upload via Excel',
    description: 'Import contacts directly from Excel—no reformatting needed.',
  },
  {
    icon: <HiPhotograph className="text-3xl text-blue-600" />,
    title: 'Send Attachments',
    description: 'Include images, PDFs, and more to make messages impactful.',
  },
  {
    icon: <HiUserCircle className="text-3xl text-blue-600" />,
    title: 'Personalized Messages',
    description: 'Customize each message using {{Name}} fields for better engagement.',
  },
  {
    icon: <HiClock className="text-3xl text-green-600" />,
    title: 'Time-Gap Batching',
    description: 'Add configurable delays (e.g. 5–15s) to reduce ban risk.',
  },
  {
    icon: <HiCheckCircle className="text-3xl text-green-600" />,
    title: 'Filter Contacts',
    description: 'Use filters to message only the right people.',
  },
  {
    icon: <HiDownload className="text-3xl text-green-600" />,
    title: 'Download Reports',
    description: 'Export stats of sent/failed messages and pick up where you left off.',
  },
];

const ZapBlast = () => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="max-w-7xl mx-auto px-6 py-16"
  >
    {/* Hero Section */}
    <div className="grid md:grid-cols-2 gap-10 items-center mb-20">
      <div>
        <h1 className="text-5xl font-bold text-blue-700 leading-tight mb-6">
          ZapBlast <span className="text-green-500">Bulk WhatsApp</span> Desktop App
        </h1>
        <p className="text-gray-700 text-lg mb-6">
          A powerful WhatsApp marketing tool that helps you send bulk messages using Excel uploads, personalized templates, and batch scheduling—all while keeping your account safe.
        </p>
        <Link
          to="/contact"
          className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition"
        >
          Download for Windows/Mac
        </Link>
      </div>
      <div>
        <img src={zapblastHero} alt="ZapBlast Hero" className="rounded-xl shadow-xl w-full" />
      </div>
    </div>

    {/* Screenshots With Description */}
<div className="space-y-24 mb-24">
  {/* Screenshot 1 */}
  <div className="grid md:grid-cols-2 gap-12 items-center bg-green-50 p-8 rounded-2xl shadow-md">
    <img
      src={screenshot1}
      alt="Campaign Screenshot"
      className="rounded-xl shadow-lg border border-green-200"
    />
    <div>
      <h2 className="text-3xl font-bold text-green-700 mb-4">Easy Campaign Creation</h2>
      <p className="text-gray-700 text-md leading-relaxed">
        Build high-performing WhatsApp campaigns with just a few clicks. Upload Excel contacts, add personalized messages, and use advanced delay settings to prevent bans. Everything is right at your fingertips.
      </p>
    </div>
  </div>

  {/* Screenshot 2 */}
  <div className="grid md:grid-cols-2 gap-12 items-center bg-blue-50 p-8 rounded-2xl shadow-md">
    <div>
      <h2 className="text-3xl font-bold text-blue-700 mb-4">10+ Smart WhatsApp Tools</h2>
      <p className="text-gray-700 text-md leading-relaxed">
        Automate your outreach with powerful tools like group grabbers, number filters, email scrapers, and auto responders. ZapBlast bundles everything you need to scale faster—without complexity.
      </p>
    </div>
    <img
      src={screenshot2}
      alt="Tools Screenshot"
      className="rounded-xl shadow-lg border border-blue-200"
    />
  </div>
</div>


    {/* Features Section */}
    <h2 className="text-3xl font-bold text-center text-blue-700 mb-10">Key Features</h2>
    <div className="grid md:grid-cols-3 gap-8 mb-16">
      {features.map((feature, index) => (
        <div
          key={index}
          className="bg-white rounded-xl shadow-md hover:shadow-xl transition p-6 text-center border border-gray-200"
        >
          <div className="mb-4 flex justify-center">{feature.icon}</div>
          <h3 className="text-xl font-semibold mb-2 text-gray-800">{feature.title}</h3>
          <p className="text-sm text-gray-600">{feature.description}</p>
        </div>
      ))}
    </div>

    {/* CTA */}
    <div className="text-center mt-10">
      <Link
        to="/contact"
        className="inline-block px-8 py-4 bg-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg hover:bg-blue-800 transition"
      >
        Get ZapBlast Now
      </Link>
    </div>
  </motion.section>
);

export default ZapBlast;
